import { Injectable } from '@angular/core';
import { LanguageService } from './language.service';
import { LanguageVariants } from 'src/app/config';

/**
 * Idioma de CONTENIDO: el `lang=` que se manda a los endpoints que devuelven
 * contenido localizado desde backoffice (cuestionarios, indicadores, reportes,
 * sociograma, chatbot, etc).
 *
 * Históricamente esta clase mantenía su propio storage (`content_language`)
 * porque el front no tenía diccionario `es-LA` para la interfaz: el backend
 * podía servir contenido es-LA mientras la UI seguía forzosamente en es-ES.
 * Desde que existe `assets/i18n/es-LA.json`, el locale de interfaz ya resuelve
 * la variante por país y ambas cosas dejaron de poder divergir — así que esto
 * pasa a ser una fachada sobre `LanguageService`.
 *
 * Se conserva como clase propia (y no se reemplazan las ~15 inyecciones) para
 * no perder la distinción semántica en los call sites: los servicios piden
 * "idioma de contenido", que es una decisión de producto, no el locale con el
 * que se pinta un botón.
 */
@Injectable({
  providedIn: 'root',
})
export class ContentLanguageService {
  constructor(private languageService: LanguageService) {}

  /** Idioma para el parámetro `lang=` de los endpoints de contenido. */
  getContentLang(): string {
    return this.languageService.getLocale();
  }

  /**
   * Se llama apenas se conoce la organización del usuario logueado (ver
   * `UserService.getUserProfileData`). El locale lo resuelve el backend y lo
   * manda en `language_variants`; acá solo se aplica, para la interfaz y —por
   * lo tanto— también para el contenido.
   */
  setContentLangFromOrganization(
    organization: { language_variants?: LanguageVariants } | null | undefined,
  ): void {
    void this.languageService.applyLanguageVariants(organization?.language_variants);
  }

  /** Llamar al cerrar sesión, para no arrastrarle el país de un usuario al
   *  siguiente que se loguee en el mismo dispositivo. */
  clear(): void {
    this.languageService.resetResolvedLocale();
  }
}
